import React, { useCallback, useEffect, useRef, useState } from "react";
import MypageLayout from "./MypageLayout";
import { emitToast } from "./Toast";
import ErrorBar from "./ErrorBar";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import ConfirmDialog from "./ConfirmDialog";
import useEscapeKey from "./useEscapeKey";
import PdfViewer from "./PdfViewer";
import apiErrorMessage from "./apiError";
import { FOUNDED_YM, MONTHS_AHEAD } from "./dateUtil";
import { formHeaders, jsonHeaders } from "./csrf";

// 給与明細 アップロード/管理ページ（管理者・セッション認証）。Porcelain テーマ。
// UI（2026-07会議で確定）：行=年月／列=社員のグリッド。
//   ・未アップロードのセル → 破線ドロップ枠（PDFをドラッグ、またはクリックで選択）
//   ・アップロード済みのセル → PDF（閲覧）｜削除
// 交付日は表示しない。社員は在籍者ぶんの列を出す。
const API_URL = process.env.REACT_APP_API_URL || "";

// 会社設立月。この月〜「当月+MONTHS_AHEAD」ぶんのアップロード枠を出す（データが無い月も遡り/先行登録できる）。
// 株式会社Guluu = 2023年11月7日 法人設立（法人番号 7120001259658、法人番号指定日）。
// 当月より先も何ヶ月ぶん枠を出すか（翌月・翌々月分を先に用意できるように）。

const IconUpload = () => (<svg viewBox="0 0 24 24"><path d="M12 16V5M8 9l4-4 4 4" /><path d="M5 19h14" /></svg>);
const IconFile = () => (<svg viewBox="0 0 24 24"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>);
const IconTrash = () => (<svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>);
const IconChevron = () => (<svg className="mp-psyr-chev" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>);
const IconMoneybag = () => (<svg className="mp-psgift" viewBox="0 0 24 24"><path d="M9.5 3h5a1.5 1.5 0 0 1 1.5 1.5a3.5 3.5 0 0 1 -3.5 3.5h-1a3.5 3.5 0 0 1 -3.5 -3.5a1.5 1.5 0 0 1 1.5 -1.5z" /><path d="M4 17v-1a8 8 0 1 1 16 0v1a4 4 0 0 1 -4 4h-8a4 4 0 0 1 -4 -4z" /></svg>);
const IconPlusMini = () => (<svg className="mp-psplusmini" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>);

// 対象月の一覧を「当月+MONTHS_AHEAD → 会社設立月(FOUNDED_YM)」まで降順で作る（6桁 "YYYYMM"）。
// データの無い月も枠を出す（遡り登録・翌月以降の事前準備のため）。念のためデータ側の月も併合する。
function buildMonths(payslips) {
  const now = new Date();
  const startY = parseInt(FOUNDED_YM.slice(0, 4), 10);
  const startM = parseInt(FOUNDED_YM.slice(4, 6), 10);
  const set = new Set(payslips.map((p) => p.year_month));
  // 起点＝当月からMONTHS_AHEADヶ月先。12月を超えたら翌年へ繰り上げる。
  let y = now.getFullYear();
  let m = now.getMonth() + 1 + MONTHS_AHEAD;
  while (m > 12) { m -= 12; y += 1; }
  // 起点から設立月まで1ヶ月ずつ遡って追加。
  while (y > startY || (y === startY && m >= startM)) {
    set.add(`${y}${String(m).padStart(2, "0")}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return [...set].sort().reverse();
}
// 通知・確認・aria 用（年つき）。
function ymLabel(ym) {
  if (!ym || ym.length !== 6) return ym || "-";
  return `${ym.slice(0, 4)}年${parseInt(ym.slice(4, 6), 10)}月`;
}
// 年アコーディオンのセクション内は年が見出しにあるので月のみ。
function mLabel(ym) {
  if (!ym || ym.length !== 6) return ym || "-";
  return `${parseInt(ym.slice(4, 6), 10)}月`;
}

// 未アップロードのセル：破線ドロップ枠。ドラッグ&ドロップ or クリックでPDFを選択。
function UploadCell({ emp, ym, onFile }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const pick = () => inputRef.current && inputRef.current.click();
  return (
    <div
      className={`mp-psdrop${drag ? " mp-drag" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`${emp.name} ${ymLabel(ym)} の給与明細をアップロード`}
      onClick={pick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) onFile(emp, ym, f); }}
    >
      <IconUpload /> アップロード
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files[0]; if (f) onFile(emp, ym, f); e.target.value = ""; }}
      />
    </div>
  );
}

function PayslipAdminPage() {
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 削除の確認ダイアログ（対象の明細。null=閉じている）。誤操作防止。
  const [confirmRow, setConfirmRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // PDF閲覧モーダル（表示中の明細。null=閉じている）。社員側と同じオーバーレイ埋め込み。
  const [viewing, setViewing] = useState(null);
  // 年アコーディオンで開いている年のSet（null=未初期化）。初期は「PDFがある最新の年」だけ開く。
  const [openYears, setOpenYears] = useState(null);
  // 追加した賞与枠（まだ誰もアップロードしていない枠も表示するため保持）。[{year_month, label}]
  const [addedFrames, setAddedFrames] = useState([]);
  // 賞与枠を追加するダイアログ。{year, label, month} | null。
  const [bonusDialog, setBonusDialog] = useState(null);
  // 賞与枠ごと削除の確認（対象フレーム {year_month, label}）。null=閉じている。
  const [confirmBonusFrame, setConfirmBonusFrame] = useState(null);
  const [deletingFrame, setDeletingFrame] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [empRes, listRes] = await Promise.all([
        fetch(`${API_URL}/api/employee/list/`, { credentials: "include" }),
        fetch(`${API_URL}/api/employee/payslips/list/`, { credentials: "include" }),
      ]);
      if (empRes.status === 401 || listRes.status === 401) { setError("ログインが必要です。"); return; }
      if (empRes.status === 403 || listRes.status === 403) { setError("管理者権限が必要です。"); return; }
      if (!empRes.ok || !listRes.ok) throw new Error("取得に失敗しました");
      setEmployees(((await empRes.json()).results || []).filter((e) => e.is_active));
      setRows((await listRes.json()).results || []);
    } catch (e) {
      setError("データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // PDFをアップロード。実バックエンドはFormDataのフィールドを、デモはクエリを読む（両方送る）。
  // opts.kind==="bonus" のときは賞与＝支給月(ym)＋賞与名(opts.label)で1枠として登録する。
  const onFile = async (emp, ym, file, opts = {}) => {
    setError("");
    const isBonus = opts.kind === "bonus";
    try {
      const fd = new FormData();
      fd.append("employee_id", emp.id);
      fd.append("year_month", ym);
      fd.append("file", file);
      fd.append("kind", isBonus ? "bonus" : "monthly");
      fd.append("label", opts.label || "");
      let q = `employee_id=${emp.id}&year_month=${ym}&employee_name=${encodeURIComponent(emp.name)}`;
      if (isBonus) q += `&kind=bonus&label=${encodeURIComponent(opts.label || "")}`;
      const res = await fetch(`${API_URL}/api/employee/payslips/upload/?${q}`, {
        method: "POST", credentials: "include", headers: formHeaders(), body: fd,
      });
      // BEの理由をそのまま出す。400 は PDF不正だけでなく「賞与名は50文字以内で指定して
      // ください」「year_month は YYYYMM / YYYY-MM で…」等も含むため、PDFの話に
      // 決め打ちすると原因と無関係な文言が出る。
      if (!res.ok) { setError(await apiErrorMessage(res, "アップロードに失敗しました。")); return; }
      emitToast(`${emp.name}さんの${isBonus ? `${ymLabel(ym)} ${opts.label}` : ymLabel(ym)}をアップロードしました。`);
      await fetchAll();
    } catch (e) {
      setError("アップロードに失敗しました。");
    }
  };

  // 確認ダイアログで「削除する」を押したときの実処理。
  const doDelete = async () => {
    if (!confirmRow) return;
    const id = confirmRow.id;
    setDeleting(true); setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/payslips/delete/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, "削除に失敗しました。")); return; }
      setRows((prev) => prev.filter((r) => r.id !== id));
      emitToast("削除しました。");
    } catch (e) {
      setError("削除に失敗しました。");
    } finally {
      setDeleting(false);
      setConfirmRow(null);
    }
  };

  // Esc でPDF閲覧モーダルを閉じる（確認ダイアログ側は ConfirmDialog が内蔵）。
  useEscapeKey(() => setViewing(null), !!viewing);

  // 初期表示：PDFが実際にある最新の年だけ開く（無ければ最新の年）。以降はユーザー操作を保持。
  useEffect(() => {
    if (loading || openYears !== null) return;
    const dataYear = rows.reduce((acc, r) => {
      const y = (r.year_month || "").slice(0, 4);
      return y > acc ? y : acc;
    }, "");
    const initial = dataYear || (months[0] ? months[0].slice(0, 4) : "");
    setOpenYears(new Set(initial ? [initial] : []));
    // months は rows から算出されるため rows のみ依存で十分。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, openYears, rows]);

  // (社員ID, 年月) → 月次明細 の逆引き（賞与は除外）。同姓同名で取り違えないよう id で対応づける（#10）。
  const findPayslip = (empId, ym) => rows.find((r) => r.employee_id === empId && r.year_month === ym && r.kind !== "bonus") || null;
  // (社員ID, 年月, 賞与名) → 賞与明細 の逆引き。
  const findBonus = (empId, ym, label) => rows.find((r) => r.employee_id === empId && r.year_month === ym && r.kind === "bonus" && r.label === label) || null;
  const months = buildMonths(rows.filter((r) => r.kind !== "bonus"));

  // 賞与枠の一覧＝データにある賞与(year_month,label) ∪ 追加した空枠。年ごとにまとめる。
  const bonusFramesByYear = (() => {
    const seen = new Set();
    const frames = [];
    const add = (year_month, label) => {
      const key = `${year_month}|${label}`;
      if (!seen.has(key)) { seen.add(key); frames.push({ year_month, label }); }
    };
    rows.filter((r) => r.kind === "bonus").forEach((r) => add(r.year_month, r.label));
    addedFrames.forEach((f) => add(f.year_month, f.label));
    const map = {};
    frames.forEach((f) => { const y = f.year_month.slice(0, 4); (map[y] = map[y] || []).push(f); });
    Object.values(map).forEach((list) => list.sort((a, b) => Number(b.year_month) - Number(a.year_month) || (a.label < b.label ? -1 : 1)));
    return map;
  })();

  // 年ごとにグルーピング（months は降順なので groups も新しい年順・各年内も降順）。
  const groups = [];
  months.forEach((ym) => {
    const y = ym.slice(0, 4);
    let g = groups.find((x) => x.year === y);
    if (!g) { g = { year: y, list: [] }; groups.push(g); }
    g.list.push(ym);
  });
  const isYearOpen = (y) => !!(openYears && openYears.has(y));
  const toggleYear = (y) => setOpenYears((prev) => {
    const next = new Set(prev || []);
    if (next.has(y)) next.delete(y); else next.add(y);
    return next;
  });

  // セル内容（PDF/削除 or アップロード枠）。デスクトップ表・モバイルカードで共用。
  // bonusLabel を渡すと賞与セル（その支給月×賞与名の枠）として扱う。
  const cellContent = (emp, ym, bonusLabel) => {
    const ps = bonusLabel ? findBonus(emp.id, ym, bonusLabel) : findPayslip(emp.id, ym);
    return ps ? (
      <div className="mp-pscell">
        <button
          className="mp-pspdf"
          onClick={() => { if (ps.url) setViewing(ps); }}
        ><IconFile /> PDF</button>
        <span className="mp-psbar" aria-hidden="true">|</span>
        <button className="mp-psdel" onClick={() => setConfirmRow(ps)}><IconTrash /> 削除</button>
      </div>
    ) : (
      <UploadCell emp={emp} ym={ym} onFile={bonusLabel ? (e, y, f) => onFile(e, y, f, { kind: "bonus", label: bonusLabel }) : onFile} />
    );
  };

  // 賞与枠を追加ダイアログの確定。年＋月＋賞与名で空枠を作る（アップロードすればデータ化）。
  const addBonusFrame = () => {
    if (!bonusDialog || !bonusDialog.label.trim() || !bonusDialog.month) return;
    const ym = `${bonusDialog.year}${String(bonusDialog.month).padStart(2, "0")}`;
    const label = bonusDialog.label.trim();
    setAddedFrames((prev) => (prev.some((f) => f.year_month === ym && f.label === label) ? prev : [...prev, { year_month: ym, label }]));
    setBonusDialog(null);
  };

  // 賞与枠ごと削除：その枠の全社員の賞与明細を削除し、空枠(addedFrames)からも除去。
  const deleteBonusFrame = async () => {
    if (!confirmBonusFrame) return;
    const { year_month, label } = confirmBonusFrame;
    setDeletingFrame(true); setError("");
    try {
      // BE側でトランザクション一括削除（1件ずつ→途中失敗で部分削除、を防ぐ・#11）。
      const res = await fetch(`${API_URL}/api/employee/payslips/bonus-frame/delete/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ year_month, label }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, "削除に失敗しました。")); return; }
      setAddedFrames((prev) => prev.filter((f) => !(f.year_month === year_month && f.label === label)));
      emitToast(`${label}（${parseInt(year_month.slice(4, 6), 10)}月）を削除しました。`);
      await fetchAll();
    } catch (e) {
      setError("賞与枠の削除に失敗しました。");
    } finally {
      setDeletingFrame(false);
      setConfirmBonusFrame(null);
    }
  };

  return (
    <MypageLayout active="payslip" title="給与明細">
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      {loading ? (
        <Loader />
      ) : employees.length === 0 ? (
        <EmptyState>社員が登録されていません</EmptyState>
      ) : (
        <>
          {/* デスクトップ：行=月／列=社員のグリッド。年ごとにアコーディオンで開閉。 */}
          <div className="mp-tablewrap mp-only-desktop">
            <table className="mp-psgrid">
              <thead>
                <tr>
                  <th className="mp-psym">月</th>
                  {employees.map((emp) => (<th key={emp.id}>{emp.name}</th>))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const open = isYearOpen(g.year);
                  return (
                    <React.Fragment key={g.year}>
                      <tr className="mp-psyr">
                        <td colSpan={1 + employees.length}>
                          <button
                            type="button"
                            className={`mp-psyr-btn${open ? " mp-open" : ""}`}
                            onClick={() => toggleYear(g.year)}
                            aria-expanded={open}
                          >
                            <IconChevron />{g.year}年
                          </button>
                        </td>
                      </tr>
                      {open && g.list.map((ym) => (
                        <tr key={ym}>
                          <td className="mp-psym">
                            <span className="mp-psmwrap">
                              <span className="mp-psmlabel">{mLabel(ym)}</span>
                              <button type="button" className="mp-psbadd" aria-label={`${mLabel(ym)}に賞与を追加`} title="この月に賞与を追加" onClick={() => setBonusDialog({ year: g.year, month: parseInt(ym.slice(4, 6), 10), label: "" })}>
                                <IconPlusMini /><IconMoneybag />
                              </button>
                            </span>
                          </td>
                          {employees.map((emp) => (
                            <td key={emp.id}>{cellContent(emp, ym)}</td>
                          ))}
                        </tr>
                      ))}
                      {open && (bonusFramesByYear[g.year] || []).length > 0 && (
                        <>
                          <tr className="mp-psbrow">
                            <td colSpan={1 + employees.length}><span className="mp-psblabel"><IconMoneybag /> 賞与</span></td>
                          </tr>
                          {bonusFramesByYear[g.year].map((f) => (
                            <tr key={`${f.year_month}|${f.label}`}>
                              <td className="mp-psym">
                                <div className="mp-psbname-row">
                                  <span className="mp-psbname">{f.label}</span>
                                  <button type="button" className="mp-psbframe-del" aria-label="この賞与枠を削除" title="この賞与枠を削除" onClick={() => setConfirmBonusFrame(f)}><IconTrash /></button>
                                </div>
                                <div className="mp-psbmonth">{parseInt(f.year_month.slice(4, 6), 10)}月支給</div>
                              </td>
                              {employees.map((emp) => (
                                <td key={emp.id}>{cellContent(emp, f.year_month, f.label)}</td>
                              ))}
                            </tr>
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* モバイル：年アコーディオン → 月ごとカード → 社員行（名前＋PDF/削除 or アップロード） */}
          <div className="mp-pscards mp-only-mobile">
            {groups.map((g) => {
              const open = isYearOpen(g.year);
              return (
                <div className="mp-psyr-m" key={g.year}>
                  <button
                    type="button"
                    className={`mp-psyr-mbtn${open ? " mp-open" : ""}`}
                    onClick={() => toggleYear(g.year)}
                    aria-expanded={open}
                  >
                    <IconChevron />{g.year}年
                  </button>
                  {open && g.list.map((ym) => (
                    <div className="mp-pscard" key={ym}>
                      <div className="mp-pscard-head">
                        <span>{mLabel(ym)}</span>
                        <button type="button" className="mp-psbadd" aria-label={`${mLabel(ym)}に賞与を追加`} title="この月に賞与を追加" onClick={() => setBonusDialog({ year: g.year, month: parseInt(ym.slice(4, 6), 10), label: "" })}>
                          <IconPlusMini /><IconMoneybag />
                        </button>
                      </div>
                      <div className="mp-pscard-body">
                        {employees.map((emp) => (
                          <div className="mp-pscard-row" key={emp.id}>
                            <span className="mp-pscard-name">{emp.name}</span>
                            <div className="mp-pscard-ctrl">{cellContent(emp, ym)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {open && (bonusFramesByYear[g.year] || []).length > 0 && (
                    <>
                      <div className="mp-psbrow-m"><IconMoneybag /> 賞与</div>
                      {bonusFramesByYear[g.year].map((f) => (
                        <div className="mp-pscard" key={`${f.year_month}|${f.label}`}>
                          <div className="mp-pscard-head">
                          <span>{f.label}・{parseInt(f.year_month.slice(4, 6), 10)}月支給</span>
                          <button type="button" className="mp-psbframe-del" aria-label="この賞与枠を削除" title="この賞与枠を削除" onClick={() => setConfirmBonusFrame(f)}><IconTrash /></button>
                        </div>
                          <div className="mp-pscard-body">
                            {employees.map((emp) => (
                              <div className="mp-pscard-row" key={emp.id}>
                                <span className="mp-pscard-name">{emp.name}</span>
                                <div className="mp-pscard-ctrl">{cellContent(emp, f.year_month, f.label)}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 削除の確認ダイアログ（誤操作防止）。背景クリック / Esc / やめる で閉じる。 */}
      <ConfirmDialog
        open={!!confirmRow}
        ariaLabel="削除の確認"
        title="この明細を削除しますか？"
        onClose={() => setConfirmRow(null)}
        onConfirm={doDelete}
        confirmLabel={deleting ? "削除中…" : "削除する"}
        confirmDisabled={deleting}
      >
        {confirmRow && `${confirmRow.employee_name}さんの${confirmRow.kind === "bonus" ? `${confirmRow.year_month.slice(0, 4)}年 ${confirmRow.label}` : `${ymLabel(confirmRow.year_month)}分の給与明細`}を削除します。この操作は元に戻せません。`}
      </ConfirmDialog>

      {/* 賞与枠ごと削除の確認（登録済みの全員分が消える）。 */}
      <ConfirmDialog
        open={!!confirmBonusFrame}
        ariaLabel="賞与枠の削除確認"
        title="この賞与枠を削除しますか？"
        onClose={() => setConfirmBonusFrame(null)}
        onConfirm={deleteBonusFrame}
        confirmLabel={deletingFrame ? "削除中…" : "削除する"}
        confirmDisabled={deletingFrame}
      >
        {confirmBonusFrame && `${confirmBonusFrame.year_month.slice(0, 4)}年 ${confirmBonusFrame.label}（${parseInt(confirmBonusFrame.year_month.slice(4, 6), 10)}月）を削除します。登録済みの全員分の賞与明細も削除され、元に戻せません。`}
      </ConfirmDialog>

      {/* PDF閲覧モーダル（社員側と同じ埋め込み表示）。背景クリック / Esc / 閉じる で閉じる。 */}
      {viewing ? (
        <div className="mp-modal-back" onClick={() => setViewing(null)}>
          <div className="mp-ps-modal" role="dialog" aria-modal="true" aria-label="給与明細" onClick={(e) => e.stopPropagation()}>
            <div className="mp-ps-modal-head">
              <div className="mp-ps-modal-title">{viewing.employee_name}さん・{viewing.kind === "bonus" ? `${viewing.year_month.slice(0, 4)}年 ${viewing.label}` : `${ymLabel(viewing.year_month)}の給与明細`}</div>
              <button className="mp-ps-modal-x" onClick={() => setViewing(null)} aria-label="閉じる">×</button>
            </div>
            {/* PDF.js でアプリ内描画（ツールバー無し・横幅フィット）。失敗時は下のボタンにフォールバック。 */}
            <PdfViewer url={viewing.url} />
            <div className="mp-ps-modal-foot">
              <button className="mp-btn mp-ghost mp-sm" onClick={() => window.open(viewing.url, "_blank", "noopener")}>新しいタブで開く</button>
              <a className="mp-btn mp-primary mp-sm" href={viewing.url} download={`給与明細_${viewing.employee_name}_${viewing.year_month}.pdf`}>ダウンロード</a>
            </div>
          </div>
        </div>
      ) : null}

      {/* 賞与枠を追加ダイアログ（賞与名＋支給月を入力）。空枠を作り、各社員のアップロード枠が並ぶ。 */}
      {bonusDialog ? (
        <div className="mp-modal-back" onClick={() => setBonusDialog(null)}>
          <div className="mp-ps-modal mp-psbdlg" role="dialog" aria-modal="true" aria-label="賞与枠を追加" onClick={(e) => e.stopPropagation()}>
            <div className="mp-ps-modal-head">
              <div className="mp-ps-modal-title">{bonusDialog.year}年{bonusDialog.month}月 の賞与を追加</div>
              <button className="mp-ps-modal-x" onClick={() => setBonusDialog(null)} aria-label="閉じる">×</button>
            </div>
            <div className="mp-psbdlg-body">
              <div className="mp-field">
                <label className="mp-flabel">支給月</label>
                <div className="mp-psbdlg-month">{bonusDialog.year}年{bonusDialog.month}月</div>
              </div>
              <div className="mp-field">
                <label className="mp-flabel">賞与名</label>
                <input
                  className="mp-input"
                  value={bonusDialog.label}
                  placeholder="夏季賞与、決算賞与 など"
                  onChange={(e) => setBonusDialog((d) => ({ ...d, label: e.target.value }))}
                />
              </div>
              <p className="mp-psbdlg-hint" style={{ fontSize: "12px", color: "var(--mp-sec)", margin: "10px 0 0", lineHeight: 1.5 }}>
                この枠は、いずれかの社員にPDFをアップロードすると保存されます。空のまま画面を離れると枠は残りません。
              </p>
            </div>
            <div className="mp-ps-modal-foot">
              <button className="mp-btn mp-ghost mp-sm" onClick={() => setBonusDialog(null)}>やめる</button>
              <button className="mp-btn mp-primary mp-sm" disabled={!bonusDialog.label.trim()} onClick={addBonusFrame}>追加する</button>
            </div>
          </div>
        </div>
      ) : null}
    </MypageLayout>
  );
}

export default PayslipAdminPage;
